/**
 * Date helpers that always use the user's LOCAL timezone (not UTC).
 *
 * Why this exists:
 *
 *   `new Date().toISOString().split('T')[0]` was used in many places to get
 *   "today's date as YYYY-MM-DD". That is WRONG outside of UTC: a user in
 *   Mexico City (UTC-6) at 8 PM local sees a Date whose ISO string is
 *   already on the next UTC day. So a meal logged at 8 PM gets saved with
 *   tomorrow's date, breaks the "Today/Yesterday" navigator, and corrupts
 *   the calendar heatmap data.
 *
 *   Always use these helpers for date strings the user perceives as a
 *   calendar day (food logs, workout completions, photo months, etc.).
 */

/** Format a Date as `YYYY-MM-DD` in the local timezone. */
export function getLocalDateString(d: Date = new Date()): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** Today's date in local timezone as `YYYY-MM-DD`. */
export function getTodayLocalString(): string {
  return getLocalDateString(new Date());
}

/** Date string N days from today (positive = future, negative = past) in local time. */
export function addDaysLocalString(days: number, from: Date = new Date()): string {
  const d = new Date(from);
  d.setDate(d.getDate() + days);
  return getLocalDateString(d);
}

/** Yesterday's date in local timezone. */
export function getYesterdayLocalString(): string {
  return addDaysLocalString(-1);
}

/**
 * Today's day-of-week in Spanish, ASCII (lunes/martes/.../domingo).
 * Used to filter meal plans by the day they apply to.
 */
export function getTodayDayOfWeek(): string {
  const days = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];
  return days[new Date().getDay()];
}
